import React, { useCallback, useMemo, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { complaintAPI } from '../../services/api';
import useReport from '../reports/shared/useReport';
import ChartPanel from '../reports/shared/ChartPanel';
import KpiStrip from '../reports/shared/KpiStrip';
import ReportTable from '../reports/shared/ReportTable';
import PrintButton from '../reports/shared/PrintButton';
import DateRangeFilter, { getPresetRange } from '../reports/shared/DateRangeFilter';
import { GRID, PALETTE, axisProps, tooltipStyle } from '../reports/shared/chartTheme';
import { kpiMap } from '../reports/attendance/kpis';
import { formatClinicDate } from '../../utils/clinicTime';
import { roleLabel } from '../attendance/attendanceStatus';
import { OUTCOME, categoryLabel } from './complaintStatus';

// What the complaints add up to over a period: how many arrived, how many are still open, how long
// they take to settle, what they were about, and how they were decided. Every figure comes from the
// server's own count, so this screen can never disagree with the complaints list behind it.
//
// It lives in the Complaints page rather than the Reports module on purpose. Reports are gated by
// report group, and a report group is held by people these complaints are about.
//
// The one thing this screen must not get wrong is the staff table. A complaint keeps its link to the
// person it named whatever the outcome, which is only acceptable because mentioned, upheld and not
// upheld are counted as three separate things: an unproven complaint is a thing that was said, not a
// strike, and must never be readable as one. So there is no combined total anywhere, nothing is
// ranked by how often somebody is named, only Upheld is coloured, and the table says all of this in
// its own footer rather than leaving the reader to assume.

const pct = (v) => (v == null ? '-' : `${Number(v).toFixed(1)}%`);

/** "2 days" / "2.5 days", because the median of an odd count of days lands on a half. */
const days = (v) => {
  const n = Number(v);
  if (v == null || !isFinite(n)) return '-';
  return `${Number.isInteger(n) ? n : n.toFixed(1)} ${n === 1 ? 'day' : 'days'}`;
};

/** "Sep 2026" from a "YYYY-MM" bucket, through the clinic parser so the month cannot slip a day. */
const monthLabel = (m) => formatClinicDate(`${m}-01`, { day: undefined, month: 'short', year: 'numeric' }, m);

/**
 * Upheld is the only number here that is a finding, so it is the only one that carries a colour. A
 * zero stays grey rather than reading as an achievement, and a missing count shows as nothing at all
 * instead of a 0 nobody counted.
 */
const upheldCell = (v) => {
  if (v == null) return '-';
  return Number(v) > 0
    ? <span className="font-semibold text-red-700">{v}</span>
    : <span className="text-gray-400">{v}</span>;
};

const CATEGORY_COLUMNS = [
  {
    key: 'label', label: 'Category', wrap: true,
    render: (r) => r.label || categoryLabel({ category: r.category }),
    csv: (r) => r.label || categoryLabel({ category: r.category }),
    searchText: (r) => `${r.label || ''} ${r.category || ''}`,
  },
  { key: 'received', label: 'Received', align: 'right' },
  { key: 'upheld', label: 'Upheld', align: 'right', render: (r) => upheldCell(r.upheld), csv: (r) => r.upheld },
  { key: 'notUpheld', label: 'Not upheld', align: 'right' },
];

// The server sends the words for a category and a decision, so the patient's form, the complaint
// itself and this report can never offer different ones. complaintStatus holds the fallbacks, which
// is also what the detail modal names a decision by.
const outcomeText = (r) => r.label || OUTCOME[r.outcome]?.label || r.outcome;

const OUTCOME_COLUMNS = [
  {
    key: 'label', label: 'Decision', wrap: true,
    render: outcomeText,
    csv: outcomeText,
    searchText: (r) => `${outcomeText(r)} ${r.outcome || ''}`,
  },
  { key: 'count', label: 'Complaints', align: 'right' },
];

const STAFF_COLUMNS = [
  { key: 'userName', label: 'Name', wrap: true },
  { key: 'role', label: 'Role', render: (r) => roleLabel(r.role), csv: (r) => roleLabel(r.role) },
  { key: 'mentioned', label: 'Mentioned', align: 'right' },
  { key: 'upheld', label: 'Upheld', align: 'right', render: (r) => upheldCell(r.upheld), csv: (r) => r.upheld },
  { key: 'notUpheld', label: 'Not upheld', align: 'right' },
];

const ComplaintsOverview = () => {
  // Six months by default: the chart is a month per bar, and a complaints month at this clinic is a
  // handful of rows, so a single month would be one pair of bars and no trend at all.
  const [range, setRange] = useState(() => getPresetRange('last6Months'));
  const [preset, setPreset] = useState('last6Months');

  const fetcher = useCallback(
    () => complaintAPI.overview({ from: range.from, to: range.to }),
    [range],
  );
  const { data, loading } = useReport(fetcher);

  const byCategory = useMemo(() => data?.byCategory || [], [data]);
  const byOutcome = useMemo(() => data?.byOutcome || [], [data]);
  // Left in the order the server sent: by upheld first, then by how often somebody was named. The
  // browser deliberately does not re-rank this list.
  const byStaff = useMemo(() => data?.byStaff || [], [data]);
  const byMonth = useMemo(() => data?.byMonth || [], [data]);

  const k = kpiMap(data);
  const overdue = k.overdue ?? 0;
  const kpis = [
    { label: 'Received', value: k.received, sub: 'Complaints that arrived' },
    { label: 'Still open', value: k.open, sub: 'New or under review' },
    { label: 'Overdue', value: overdue, sub: 'Past the date severity set', tone: Number(overdue) > 0 ? 'alert' : undefined },
    { label: 'Resolved', value: k.resolved, sub: 'Looked into and closed' },
    { label: 'Median to resolve', value: days(k.medianDaysToResolve), sub: 'Half took less, half more' },
    { label: 'Upheld', value: pct(k.upheldPercent), sub: 'Of the ones decided' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      {/* Two series, so the colours are the legend: one per series rather than one per bar. */}
      <ChartPanel title="Complaints received and resolved">
        {loading || byMonth.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">
            {loading ? 'Loading' : 'No complaint arrived in the selected range'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byMonth} margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke={GRID} strokeDasharray="2 3" vertical={false} />
              <XAxis dataKey="month" tickFormatter={monthLabel} {...axisProps} />
              <YAxis allowDecimals={false} {...axisProps} />
              <Tooltip {...tooltipStyle} labelFormatter={monthLabel} />
              <Legend />
              <Bar dataKey="received" name="Received" fill={PALETTE[0]} radius={[4, 4, 0, 0]} />
              <Bar dataKey="resolved" name="Resolved" fill={PALETTE[1]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        title="Complaints by category"
        columns={CATEGORY_COLUMNS}
        rows={byCategory}
        csvName={`complaints-by-category-${range.from}-to-${range.to}`}
        emptyText="No complaint arrived in the selected range"
        footnote="Upheld and not upheld count only the complaints in a category that have been decided, so the two need not add up to the number received: the rest are still being looked into."
      />

      <ReportTable
        title="How they were decided"
        columns={OUTCOME_COLUMNS}
        rows={byOutcome}
        csvName={`complaints-by-outcome-${range.from}-to-${range.to}`}
        emptyText="No complaint in the selected range has been decided yet"
        footnote="A complaint carries a decision only once it has been closed, so these need not add up to the number received. Resolved and not upheld is a real outcome: it was looked into, it was not substantiated, and the complainant was told."
      />

      <ReportTable
        title="Staff mentioned"
        columns={STAFF_COLUMNS}
        rows={byStaff}
        csvName={`complaints-by-staff-${range.from}-to-${range.to}`}
        emptyText="No complaint in the selected range has named anybody"
        footnote="Mentioned means a complaint named this person. It is not a finding. Only upheld counts a complaint that was looked into and substantiated, so a complaint that was not upheld is not a mark against anybody. The list is ordered by upheld, never by how often somebody is named."
      />
    </div>
  );
};

export default ComplaintsOverview;
